/**
 * 报告生成 Hook
 * 支持真正的 SSE 流式生成
 */

import { useState, useCallback, useRef } from 'react';
import {
  streamGenerateReport,
  getReportContent,
  downloadReport as apiDownloadReport,
  ProgressInfo,
  StreamResult
} from '../utils/api';

export interface ReportContent {
  no_reference?: string;
  with_reference?: string;
}

export const useReportGeneration = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [streamContent, setStreamContent] = useState('');
  const [progress, setProgress] = useState<ProgressInfo | null>(null);
  const [reportContent, setReportContent] = useState<ReportContent>({});
  const [error, setError] = useState<string>('');
  const [currentTaskId, setCurrentTaskId] = useState<string>('');
  const [currentReports, setCurrentReports] = useState<StreamResult['reports']>({});
  const [isComplete, setIsComplete] = useState(false);

  const abortControllerRef = useRef<(() => void) | null>(null);
  const fullContentRef = useRef('');

  /**
   * 生成报告（流式）
   */
  const generateReport = useCallback(async (query: string): Promise<string | null> => {
    // 重置状态
    setIsGenerating(true);
    setStreamContent('');
    fullContentRef.current = '';
    setProgress(null);
    setReportContent({});
    setError('');
    setCurrentTaskId('');
    setCurrentReports({});
    setIsComplete(false);

    return new Promise<string | null>((resolve) => {
      const cancel = streamGenerateReport(query, {
        onContent: (content: string) => {
          setStreamContent((prev) => prev + content);
          fullContentRef.current += content;
        },

        onProgress: (progressInfo: ProgressInfo) => {
          setProgress(progressInfo);
        },

        onComplete: async (result: StreamResult) => {
          setIsGenerating(false);
          setIsComplete(true);
          setProgress({
            stage: 'complete',
            status: 'completed',
            message: '报告生成完成',
            percent: 100,
          });

          setCurrentTaskId(result.taskId);
          setCurrentReports(result.reports);

          // 获取完整报告内容
          try {
            if (result.taskId) {
              const reportData = await getReportContent(result.taskId);
              setReportContent(reportData.reports || {});
            }
          } catch (err) {
            console.error('获取报告内容失败:', err);
          }

          resolve(result.taskId);
        },

        onError: (errorMessage: string) => {
          setIsGenerating(false);
          setError(errorMessage);
          setProgress({
            stage: 'error',
            status: 'error',
            message: errorMessage,
          });
          resolve(null);
        },
      });

      abortControllerRef.current = cancel;
    });
  }, []);

  /**
   * 取消生成
   */
  const cancelGeneration = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current();
      abortControllerRef.current = null;
    }
    setIsGenerating(false);
    setIsComplete(false);
    setProgress({
      stage: 'cancelled',
      status: 'cancelled',
      message: '已取消',
    });
  }, []);

  /**
   * 下载报告
   */
  const downloadReport = useCallback(
    (taskId?: string, reportType: 'no_reference' | 'with_reference' = 'no_reference', format: 'md' | 'pdf' = 'md') => {
      const targetTaskId = taskId || currentTaskId;
      if (!targetTaskId) {
        alert('没有可下载的报告');
        return;
      }
      apiDownloadReport(targetTaskId, reportType, format);
    },
    [currentTaskId]
  );

  /**
   * 加载历史报告内容
   */
  const loadReportContent = useCallback(async (taskId: string): Promise<boolean> => {
    try {
      const reportData = await getReportContent(taskId);
      setReportContent(reportData.reports || { no_reference: '报告内容为空' });
      setCurrentTaskId(taskId);
      setError('');
      setIsComplete(true);
      return true;
    } catch (err) {
      console.error('加载历史报告内容失败:', err);
      setError('加载历史报告失败');
      return false;
    }
  }, []);

  /**
   * 重置状态
   */
  const reset = useCallback(() => {
    setIsGenerating(false);
    setStreamContent('');
    fullContentRef.current = '';
    setProgress(null);
    setReportContent({});
    setError('');
    setCurrentTaskId('');
    setCurrentReports({});
    setIsComplete(false);
    if (abortControllerRef.current) {
      abortControllerRef.current();
      abortControllerRef.current = null;
    }
  }, []);

  return {
    // 状态
    isGenerating,
    streamContent,
    progress,
    reportContent,
    error,
    currentTaskId,
    currentReports,
    isComplete,

    // 方法
    generateReport,
    cancelGeneration,
    downloadReport,
    loadReportContent,
    reset,
  };
};
